import { BrowserRouter } from "react-router-dom";
import AppContextProvider from "@lib/context/AppContextProvider";
import AppThemeProvider from "@lib/context/AppThemeProvider";
import AppLocaleProvider from "@lib/context/AppLocaleProvider";
import WebSocketDataProvider from "@lib/context/WebSocketDataProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider as ReduxProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import store, { persistor } from "@lib/redux";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
import TimeZone from "dayjs/plugin/timezone";
import CustomParse from "dayjs/plugin/customParseFormat";

import "@lib/mockapi";
import "@assets/styles/index.css";
import "react-toastify/dist/ReactToastify.css";
import "@xyflow/react/dist/style.css";
// require('dayjs/locale/de')
import "dayjs/locale/tr";
import isBetween from "dayjs/plugin/isBetween";
import { isDev } from "@lib/helpers/General";
import AppPwaProvider from "@lib/context/AppPwaProvider";
import LocaleContextProvider from "@lib/context/AppContextProvider/LocaleContextProvider";
import App from "./App";
import AppVersionProvider from "@lib/context/AppVersionProvider";
import { Suspense } from "react";
import { AppLoader } from "@lib/components/AppComponents";

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(CustomParse);
dayjs.extend(TimeZone);
dayjs.extend(utc);
dayjs.extend(isBetween);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
      staleTime: 1000 * 60 * 10,
    },
  },
});

const AppProviders: React.FC = () => {
  return (
    <Suspense fallback={<AppLoader />}>
      <ReduxProvider store={store}>
        <PersistGate persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            {isDev() && <ReactQueryDevtools />}

            <AppThemeProvider>
              <LocaleContextProvider>
                <AppLocaleProvider>
                  <BrowserRouter>
                    <WebSocketDataProvider>
                      <AppPwaProvider />
                      <AppVersionProvider />

                      <AppContextProvider>
                        <App />
                      </AppContextProvider>
                    </WebSocketDataProvider>
                  </BrowserRouter>
                </AppLocaleProvider>
              </LocaleContextProvider>
            </AppThemeProvider>
          </QueryClientProvider>
        </PersistGate>
      </ReduxProvider>
    </Suspense>
  );
};

export default AppProviders;
